import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { OverviewPage } from './pages/OverviewPage';
import { LiveScansPage } from './pages/LiveScansPage';
import { HistoryPage } from './pages/HistoryPage';
import { GeneratePage } from './pages/GeneratePage';
import { SettingsPage } from './pages/SettingsPage';
import { StatsPage } from './pages/StatsPage';
import { DocsPage } from './pages/DocsPage';
import { LoginPage } from './pages/LoginPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { AdminOnly } from './components/PermissionGate';
import { useAuth } from './hooks/useAuth';
import {
  isAdsEnabled,
  isInteriorEnabled,
  isMobileEnabled,
  isVideoEnabled,
} from './utils/api';
import { VideoLiveScansPage } from './video/pages/LiveScansPage';
import { VideoHistoryPage } from './video/pages/HistoryPage';
import { VideoSettingsPage } from './video/pages/SettingsPage';
import { VideoOverviewPage } from './video/pages/OverviewPage';
import { VideoStatsPage } from './video/pages/StatsPage';
import { VideoDocsPage } from './video/pages/DocsPage';
import { InteriorOverviewPage } from './interior/pages/OverviewPage';
import { InteriorLiveScansPage } from './interior/pages/LiveScansPage';
import { InteriorHistoryPage } from './interior/pages/HistoryPage';
import { InteriorStatsPage } from './interior/pages/StatsPage';
import { InteriorSettingsPage } from './interior/pages/SettingsPage';
import { InteriorDocsPage } from './interior/pages/DocsPage';
import { InteriorGeneratePage } from './interior/pages/GeneratePage';
import { ManualVerificationPage } from './interior/pages/ManualVerificationPage';
import { ReviewScanPage } from './interior/pages/ReviewScanPage';
import { AdsOverviewPage } from './ads/pages/OverviewPage';
import { AdsLiveScansPage } from './ads/pages/LiveScansPage';
import { AdsHistoryPage } from './ads/pages/HistoryPage';
import { AdsStatsPage } from './ads/pages/StatsPage';
import { AdsSettingsPage } from './ads/pages/SettingsPage';
import { AdsGeneratePage } from './ads/pages/GeneratePage';
import { MobileOverviewPage } from './mobile/pages/OverviewPage';
import { MobileLiveScansPage } from './mobile/pages/LiveScansPage';
import { MobileHistoryPage } from './mobile/pages/HistoryPage';
import { MobileStatsPage } from './mobile/pages/StatsPage';
import { MobileSettingsPage } from './mobile/pages/SettingsPage';
import { MobileGeneratePage } from './mobile/pages/GeneratePage';
import { MobileDocsPage } from './mobile/pages/DocsPage';
import { EndpointsPage } from './apigen/pages/EndpointsPage';
import { EndpointPage } from './apigen/pages/EndpointPage';
import { StatisticsPage } from './apigen/pages/StatisticsPage';
import { StressPage } from './apigen/stress/StressPage';
import { StressDetailPage } from './apigen/stress/StressDetailPage';
import { TestsBankPage } from './qa/TestsBankPage';
import { RunTestPage } from './qa/RunTestPage';
import { TestsHistoryPage } from './qa/TestsHistoryPage';
import { AllServicesCostPage } from './cost/pages/AllServicesCostPage';
import { MerchCostPage } from './cost/pages/MerchCostPage';
import { ReleaseNoteVersionPage } from './release-notes/pages/ReleaseNoteVersionPage';
import { versions as releaseNotesVersions } from 'virtual:release-notes';

/**
 * Tiny helper component used by the legacy redirects. `useLocation` lets
 * us preserve the query string when bouncing `/foo?bar=1` to
 * `/lightning/foo?bar=1`, which matters for any external bookmark / link
 * still pointing at the pre-split URLs.
 */
function LegacyRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

/**
 * Top-level routing for the unified SPA.
 *
 * The shell hosts the mini-apps that are always deployed side-by-side:
 *   - `/lightning/*`  — the original lightning UI (default app)
 *   - `/video/*`      — the 360-video UI
 *   - `/interior/*`   — the 360-interior UI (apollo cropper + enhancer)
 *   - `/ads/*`        — the 360-ads UI
 *   - `/mobile/*`     — the 360-mobile UI (merch for phone-captured scans)
 *
 * The `videoEnabled` / `interiorEnabled` runtime checks below are
 * dev-mode safety nets for developers running `make web-dev` without
 * setting `VITE_VIDEO_API_BASE` / `VITE_INTERIOR_API_BASE` in their
 * `.env` — in that case the corresponding routes stay unmounted to
 * avoid surfacing a tab that would just 502. Every official build (CI
 * + `make deploy`) sets the env vars, so the routes are always present
 * in real deployments.
 *
 * Every old top-level path (`/overview`, `/live`, `/history`, …) is
 * preserved as a permanent redirect to its `/lightning/*` equivalent so
 * existing bookmarks, scripts, and external integrations don't break.
 */
function App() {
  const { isAuthenticated } = useAuth();
  const videoEnabled = isVideoEnabled();
  const interiorEnabled = isInteriorEnabled();
  const adsEnabled = isAdsEnabled();
  const mobileEnabled = isMobileEnabled();
  // Manual review is now a per-request flag (settings.manual_review), so
  // any interior job may be held for review regardless of deployment
  // config. The Manual Review screens are therefore always available.
  const manualReviewEnabled = true;
  // Release Notes has no backend/env gate — it's always available. The
  // section landing route redirects to the newest version (first entry).
  const latestReleaseVersion = releaseNotesVersions[0]?.version ?? '';

  return (
    <BrowserRouter>
      {!isAuthenticated ? (
        <LoginPage />
      ) : (
        <AppShell>
          <Routes>
            {/* ---- Lightning routes (default app) ---- */}
            <Route path="/lightning/overview" element={<OverviewPage />} />
            <Route path="/lightning/live" element={<LiveScansPage />} />
            <Route path="/lightning/history" element={<HistoryPage />} />
            <Route path="/lightning/generate" element={<GeneratePage />} />
            <Route path="/lightning/settings" element={<SettingsPage />} />
            <Route path="/lightning/docs" element={<DocsPage />} />
            <Route path="/lightning/stats" element={<StatsPage />} />
            <Route path="/lightning" element={<Navigate to="/lightning/overview" replace />} />
            <Route path="/lightning/monitor" element={<Navigate to="/lightning/live" replace />} />

            {/* ---- Video routes (always present in CI/`make deploy` builds) ---- */}
            {videoEnabled && (
              <>
                <Route path="/video/overview" element={<VideoOverviewPage />} />
                <Route path="/video/live" element={<VideoLiveScansPage refreshKey={0} />} />
                <Route path="/video/history" element={<VideoHistoryPage />} />
                <Route path="/video/stats" element={<VideoStatsPage />} />
                <Route path="/video/settings" element={<VideoSettingsPage />} />
                <Route path="/video/docs" element={<VideoDocsPage />} />
                <Route path="/video" element={<Navigate to="/video/overview" replace />} />
              </>
            )}

            {/* ---- Interior routes (always present in CI/`make deploy` builds) ---- */}
            {interiorEnabled && (
              <>
                <Route path="/interior/overview" element={<InteriorOverviewPage />} />
                <Route path="/interior/live" element={<InteriorLiveScansPage />} />
                <Route path="/interior/history" element={<InteriorHistoryPage />} />
                <Route
                  path="/interior/manual-verification"
                  element={
                    manualReviewEnabled ? (
                      <ManualVerificationPage />
                    ) : (
                      <Navigate to="/interior/history" replace />
                    )
                  }
                />
                <Route
                  path="/interior/manual-verification/:jobId"
                  element={
                    manualReviewEnabled ? (
                      <ReviewScanPage />
                    ) : (
                      <Navigate to="/interior/history" replace />
                    )
                  }
                />
                <Route path="/interior/stats" element={<InteriorStatsPage />} />
                <Route path="/interior/settings" element={<InteriorSettingsPage />} />
                <Route path="/interior/docs" element={<InteriorDocsPage />} />
                <Route path="/interior/generate" element={<InteriorGeneratePage />} />
                <Route path="/interior" element={<Navigate to="/interior/overview" replace />} />
              </>
            )}

            {/* ---- Ads routes (always present in CI/`make deploy` builds) ---- */}
            {adsEnabled && (
              <>
                <Route path="/ads/overview" element={<AdsOverviewPage />} />
                <Route path="/ads/live" element={<AdsLiveScansPage />} />
                <Route path="/ads/history" element={<AdsHistoryPage />} />
                <Route path="/ads/stats" element={<AdsStatsPage />} />
                <Route path="/ads/settings" element={<AdsSettingsPage />} />
                <Route path="/ads/generate" element={<AdsGeneratePage />} />
                <Route path="/ads" element={<Navigate to="/ads/overview" replace />} />
              </>
            )}

            {/* ---- Mobile routes (always present in CI/`make deploy` builds) ---- */}
            {mobileEnabled && (
              <>
                <Route path="/mobile/overview" element={<MobileOverviewPage />} />
                <Route path="/mobile/live" element={<MobileLiveScansPage />} />
                <Route path="/mobile/history" element={<MobileHistoryPage />} />
                <Route path="/mobile/stats" element={<MobileStatsPage />} />
                <Route path="/mobile/settings" element={<MobileSettingsPage />} />
                <Route path="/mobile/generate" element={<MobileGeneratePage />} />
                <Route path="/mobile/docs" element={<MobileDocsPage />} />
                <Route path="/mobile" element={<Navigate to="/mobile/overview" replace />} />
              </>
            )}

            {/* ---- API wrapper section (decoupled apigen service) ----
                 The `:endpoint` catch-all must stay last, or it swallows every
                 named route in this section. */}
            <Route path="/gen-api" element={<EndpointsPage />} />
            <Route path="/gen-api/stats" element={<StatisticsPage />} />
            <Route path="/gen-api/stress" element={<StressPage />} />
            <Route path="/gen-api/stress/:id" element={<StressDetailPage />} />
            <Route path="/gen-api/:endpoint" element={<EndpointPage />} />

            {/* ---- QA section (stress tests across every application) ----
                 Served by the gen-api service, which hosts the QA router. */}
            <Route path="/qa/bank" element={<TestsBankPage />} />
            <Route path="/qa/run" element={<RunTestPage />} />
            <Route path="/qa/history" element={<TestsHistoryPage />} />
            <Route path="/qa" element={<Navigate to="/qa/bank" replace />} />

            {/* ---- Cost section (cross-service cost breakdowns) ---- */}
            <Route path="/cost/services" element={<AllServicesCostPage />} />
            <Route path="/cost/merch" element={<MerchCostPage />} />
            <Route path="/cost" element={<Navigate to="/cost/services" replace />} />

            {/* ---- Administration ---- */}
            <Route
              path="/admin/users"
              element={
                <AdminOnly>
                  <UserManagementPage />
                </AdminOnly>
              }
            />

            {/* ---- Release Notes routes (always present; parsed from CHANGELOG.md) ---- */}
            <Route path="/release-notes/:version" element={<ReleaseNoteVersionPage />} />
            <Route
              path="/release-notes"
              element={
                latestReleaseVersion ? (
                  <Navigate to={`/release-notes/${latestReleaseVersion}`} replace />
                ) : (
                  <ReleaseNoteVersionPage />
                )
              }
            />

            {/* ---- Legacy redirects (preserve query/hash) ---- */}
            <Route path="/overview" element={<LegacyRedirect to="/lightning/overview" />} />
            <Route path="/live" element={<LegacyRedirect to="/lightning/live" />} />
            <Route path="/history" element={<LegacyRedirect to="/lightning/history" />} />
            <Route path="/generate" element={<LegacyRedirect to="/lightning/generate" />} />
            <Route path="/settings" element={<LegacyRedirect to="/lightning/settings" />} />
            <Route path="/stats" element={<LegacyRedirect to="/lightning/stats" />} />
            <Route path="/monitor" element={<LegacyRedirect to="/lightning/live" />} />

            {/* Default + 404: send everything to the lightning overview. */}
            <Route path="/" element={<Navigate to="/lightning/overview" replace />} />
            <Route path="*" element={<Navigate to="/lightning/overview" replace />} />
          </Routes>
        </AppShell>
      )}
    </BrowserRouter>
  );
}

export default App;
